import { Component, computed, inject } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, distinctUntilChanged, filter, map } from "rxjs";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { CategoriesAddEditComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-categories/categories-add-edit/categories-add-edit.component";
import { manageButtonProperties } from "@bitwarden/web-vault/app/gloss/manage/manage.components";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { RightPanelService } from "@bitwarden/web-vault/app/components/dialog/right-panel.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { CategoryTable } from "@bitwarden/web-vault/app/components/tables/category-tables/category.table";
import { LG_BREAKPOINT, observeSmallScreen } from "@bitwarden/web-vault/app/shared/breakpoints";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { CategorySourceEnum } from "@bitwarden/web-vault/app/models/types/category.types";
import {
  DEFAULT_CATEGORY_ID,
  GLOSS_CREDITS_CATEGORY_ID,
} from "@bitwarden/web-vault/app/models/constants/category.constants";

@Component({
  selector: "app-manage-categories",
  templateUrl: "./manage-categories.component.html",
})
export class ManageCategoriesComponent {
  private readonly rightPanel = inject(RightPanelService);
  loading = false;
  table: CategoryTable = new TablesFactory().get(TablesType.Categories, this);
  private userStore: UserStoreService = inject(UserStoreService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);
  private transactionStoreService: TransactionStoreService = inject(TransactionStoreService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  /** Mirrors categories-details' own [isOverlay] switch (see observeSmallScreen doc). */
  protected isOverlayActive = observeSmallScreen(LG_BREAKPOINT);

  // Route params are available immediately, but on a cold load (deep link, refresh, bookmark)
  // the categories store hasn't populated yet. combineLatest with existingCategories (rather
  // than a one-shot queryParams subscription) covers both timings.
  private readonly categoryId$ = this.route.queryParams.pipe(
    map((params) => params["categoryId"] as string | undefined),
    distinctUntilChanged(),
  );

  constructor(public dialog: MatDialog) {
    combineLatest([this.categoryId$, toObservable(this.existingCategories)])
      .pipe(
        filter(([categoryId, categories]) => !!categoryId && !!categories?.length),
        takeUntilDestroyed(),
      )
      .subscribe(([categoryId, categories]) => {
        const category = categories.find((cat) => cat.id === categoryId);
        if (!category) {
          return;
        }
        if (this.table.selectedCategory()?.id !== category.id) {
          this.table.onRowClicked(category);
        }
        this.clearCategoryIdFromUrl();
      });
  }

  /** Drop deep-link query param after selection; UI state lives on the table signal. */
  private clearCategoryIdFromUrl(): void {
    if (!this.route.snapshot.queryParamMap.get("categoryId")) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { categoryId: null },
      replaceUrl: true,
    });
  }

  addCategoryButton = new ActionButton({
    ...manageButtonProperties,
    text: this.i18nService.t("createNew"),
    onClick: this.addCategory.bind(this),
  });

  existingCategories = computed(() => {
    return this.userStore.categories.categoryViews();
  });

  manualCategoriesCount = computed(
    () =>
      this.existingCategories()?.filter((category) => category.source === CategorySourceEnum.manual)
        .length ?? 0,
  );

  // derived from the total so manual + system always sum to total and can't drift apart
  systemCategoriesCount = computed(
    () => (this.existingCategories()?.length ?? 0) - this.manualCategoriesCount(),
  );

  // ponytail: "categorized" = user-effort only — excludes Uncategorized default and the
  // system-assigned GLOSS Credits category (which requires no user action)
  categorizationRate = computed(() => {
    const all = this.transactionStoreService.enrichTransaction.collection() ?? [];
    // system-assigned and not re-categorisable, so credits transactions belong on
    // neither side of a user-effort rate
    const transactions = all.filter(
      (transaction) => !transaction.c_categories.includes(GLOSS_CREDITS_CATEGORY_ID),
    );
    if (!transactions.length) {
      return 0;
    }
    const categorizedCount = transactions.filter((transaction) =>
      transaction.c_categories.some((id) => id !== DEFAULT_CATEGORY_ID),
    ).length;
    return Math.round((categorizedCount / transactions.length) * 100);
  });

  categorizationRateColorClass = computed(() => {
    const rate = this.categorizationRate();
    if (rate >= 90) {
      return "tw-text-dt-success-500";
    }
    if (rate >= 60) {
      return "tw-text-dt-warning-500";
    }
    return "tw-text-dt-error-500";
  });

  statCards = computed(() => [
    {
      value: `${this.existingCategories()?.length ?? 0}`,
      labelKey: "totalCategories",
      valueClass: "tw-text-dt-text-headings",
    },
    {
      value: `${this.manualCategoriesCount()}`,
      labelKey: "manualCategories",
      valueClass: "tw-text-dt-text-headings",
    },
    {
      value: `${this.systemCategoriesCount()}`,
      labelKey: "systemCategories",
      valueClass: "tw-text-dt-text-headings",
    },
    {
      value: `${this.categorizationRate()}%`,
      labelKey: "categorizationRate",
      valueClass: this.categorizationRateColorClass(),
    },
  ]);

  searchText = "";
  noResultsFound = false;

  async addCategory() {
    // existingCategories is a computed() over the reactive categories store, which
    // updateCategoryName/save already refresh — no afterClosed() reconciliation needed.
    this.rightPanel.open(DialogWrapperComponent, {
      data: {
        title: "createNewCategory",
        showBackButton: false,
        component: CategoriesAddEditComponent,
        fullyCustomised: true,
        showHeaderCloseButton: true,
        componentInputs: {
          category: null,
          actionSucceeded: this.actionSucceeded.bind(this),
        },
      },
    });
  }

  cleanSelectedCategory() {
    this.table.selectedCategory.set(null);
  }

  onSearchChange(searchValue: string): void {
    if (!this.table?.gridApi) {
      return;
    }

    this.table.gridApi.setFilterModel({
      name: {
        type: "contains",
        filter: searchValue,
      },
    });

    // Or, if you already have other filters and want to update just one:
    // const model = this.gridApi.getFilterModel() || {};
    // model['description'] = v ? { type: 'contains', filter: v } : null;
    // this.gridApi.setFilterModel(model);

    this.table.gridApi.onFilterChanged();
    this.noResultsFound = searchValue.length > 0 && this.table.gridApi.getDisplayedRowCount() === 0;
  }

  async actionSucceeded(actionKey: { newCategoryText: string; successfulText: string }) {
    // CategoriesAddEditComponent closes the dialog itself (successfulMessage()), so there is
    // no ref to close here.
    this.cleanSelectedCategory();
    // Re-run the filter once the newly created row has been pushed into the grid on the
    // next change-detection cycle. Running it synchronously here reads a stale row count
    // and leaves the "no categories found" banner stuck (#3692).
    setTimeout(() => this.onSearchChange(this.searchText));
    this.notifyService.notify(NotificationEnum.success, "succeeded", actionKey.successfulText);
  }
}
